import BoardEmptyState from '@/components/BoardEmptyState';
import GoBackMenu from '@/components/GoBackNavbar';
import Loader from '@/components/Loader';
import Constants from '@/Constants';
import { useGet } from '@/hooks/useGet';
import { ClassType } from '@/models/ClassType';
import { CourseType } from '@/models/CourseLessonContentTO';
import { LanguageType } from '@/models/LanguageType';
import { Status } from '@/models/LessonContentTO';
import { ProgramType } from '@/models/Program';
import { ProgramClass } from '@/models/ProgramClass';
import { ApplicationState } from '@/store';
import { programsClear } from '@/store/program/action';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import ClassCard from './ClassCard';
import './style.scss';

interface Entity {
    id: number;
    classType: ClassType;
    courseType?: CourseType;
    name: string;
    locale: LanguageType;
    rootPath: string;
    type?: ProgramType;
    status: Status | undefined;
}

interface Props {
    entity: Entity;
    onAddClickHandler: () => void;
}

const Classes: React.FC<Props> = (props: Props) => {
    const { entity, onAddClickHandler } = props;

    const { strings } = useSelector((state: ApplicationState) => ({
        strings: state.locale.data,
    }));

    const orgId: number = useSelector((state: ApplicationState) => state.identity.data.user.orgId);

    const history = useHistory();

    const dispatch = useDispatch();

    const getKey = (): string => {
        if (entity.classType === 'COURSE') {
            return `${Constants.LMS_PROGRAM_CLASS_BY_COURSE_URI.replace(
                '%c',
                entity.id.toString(),
            )}?onlyCourseProgramClasses=true&withEnrollmentsCount=true`;
        }

        return `${Constants.LMS_PROGRAM_CLASS_URI}?programId=${entity.id}&orgId=${orgId}&withEnrollmentsCount=true`;
    };

    const { data: programClasses, loading, isValidating } = useGet<ProgramClass[]>(getKey(), { revalidateOnMount: true });
    const isLoading: boolean = loading || isValidating;

    useEffect(() => {
        return () => {
            dispatch(programsClear());
        };
    }, []);

    useEffect(() => {
        if (entity.status !== undefined && entity.status !== 'PUBLISHED') history.goBack();
    }, [entity.status]);

    const getClasslabel = (): string => {
        if (entity.courseType === 'WORKSHOP') {
            return strings.WORKSHOP;
        } else if (entity.courseType === 'REGULAR') {
            return strings.COURSE;
        } else if (entity.classType === 'PROGRAM') {
            return strings.PROGRAM;
        }
        return '';
    };

    return (
        <>
            <GoBackMenu goBackPath={entity.rootPath} />
            <div className="class-main-container">
                <div className="class-header">
                    <span>{strings.MANAGE_CLASSES}</span>
                    <div className="container-description">
                        <span className="item-type">{getClasslabel()}</span>
                        <h4>{entity.name}</h4>
                    </div>
                </div>

                <div className="class-box-container">
                    <div className="title-container">
                        <span>{strings.CLASSES_LIST}</span>
                    </div>

                    <div className="classes-container">
                        <div className="class-board">
                            {isLoading ? (
                                <Loader />
                            ) : (
                                <>
                                    {programClasses?.length === 0 ? (
                                        <BoardEmptyState message={strings.CREATE_NEW_COURSE_CLASS} title={strings.EMPTY_COURSE_CLASS_LIST} />
                                    ) : (
                                        programClasses?.map((programClass: ProgramClass) => (
                                            <ClassCard key={programClass.id} programClassId={programClass.id} />
                                        ))
                                    )}
                                </>
                            )}
                        </div>
                        <div className="class-menu">
                            <button className="btn btn-link new-class" disabled={isLoading} onClick={onAddClickHandler}>
                                <i className="material-icons-outlined align-middle icon-plus">{Constants.icons.ADD}</i>
                                <i className="material-icons-outlined icon align-middle icon-person">{Constants.icons.PEOPLE}</i>
                                <span className="new-class-button">{strings.CLASS}</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Classes;
